import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DynamicContentService } from '@services/dynamic-content/dynamic-content.service';
import { DynamicContentEntranceService } from '@services/dynamic-content/dynamic-content-entrance.service';
import { DynamicContentHistoryService } from '@services/dynamic-content/dynamic-content-history.service';
import { Models } from "@ibexfusion/models";
import { PageAction } from "../../../components/page-actions/page-actions.component";

export interface FieldChange {
  fieldName: string;
  type: 'title' | 'content' | 'mediaFiles' | 'dataFiles' | 'published' | 'other';
  oldValue?: any;
  newValue?: any;
}

export interface ParsedHistory {
  id: string;
  timestamp: Date;
  user: { name: string; email: string; };
  method: 'patch' | 'remove' | 'post' | string;
  itemTitle: string;
  itemType: string;
  isDeleted: boolean;
  changes: FieldChange[];
}

@Component({
  selector: 'app-dynamic-content-history',
  templateUrl: './dynamic-content-history.component.html',
  styleUrl: './dynamic-content-history.component.scss',
  providers: [DynamicContentService, DynamicContentEntranceService, DynamicContentHistoryService],
  standalone: false
})
export class DynamicContentHistoryComponent implements OnInit {
  private packageId: string;
  private entranceId: string;
  public entrance: Models.DynamicContentEntrance;
  private entryId: string;
  public entry: Models.DynamicContent;

  public histories: ParsedHistory[] = [];
  public isLoading = true;
  public pageActions: any[];

  constructor(
    private historyService: DynamicContentHistoryService, private entryService: DynamicContentService, private route: ActivatedRoute, private router: Router,
    private entranceService: DynamicContentEntranceService
  ) {}

  async ngOnInit() {
    this.packageId = this.route.snapshot.params['packageId'];
    this.entranceId = this.route.snapshot.params['entranceId'];
    this.entryId = this.route.snapshot.params['entryId'];

    this.pageActions = [new PageAction({ icon: 'subdirectory_arrow_left', title: 'Back', translationString: 'general.back', callback: () => this.backPage() })];

    let ids: string[] = [];
    let subIds: string[] = [];

    if (this.entryId) {
      this.entry = await this.entryService.get(this.entryId);
      ids = [this.entryId].concat((this.entry.translationEntries || []).concat(this.entry.children));
      subIds = (this.entry.translationEntries || []).concat(this.entry.children);
    } else if (this.entranceId) {
      this.entrance = await this.entranceService.get(this.entranceId);
      ids = [this.entranceId].concat(ids.concat(this.entrance?.children || []));
      subIds = this.entrance?.children || [];
    }

    while (subIds?.length > 0) {
      const entries = await this.entryService.find(0, 10000, { _id: { $in: subIds } });
      subIds = (entries?.data || []).map((e: any) => (e.translationEntries || []).concat(e.children)).flat();
      ids = ids.concat(subIds);
    }

    const actualEntriesRes = await this.entryService.find(0, 10000, { _id: { $in: ids } });
    const actualEntries = actualEntriesRes?.data || [];
    /*const res = await this.historyService.find(0, 10000, { $or: [{ dynamicContentId: { $in: ids } }, { parentId: { $in: ids } }] });*/
    const rawHistories = await this.fetchHistoryInBatches(ids);

    const syntheticCreations = actualEntries.filter(entry => entry.created).map(entry => ({
      _id: `synthetic_create_${entry._id}`, created: entry.created, method: 'post', newData: JSON.stringify(entry), oldData: null, firstName: '', lastName: '', email: ''
    }));

    const combined = [...rawHistories, ...syntheticCreations];

    combined.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

    this.histories = combined.map((item: any) => this.parseHistoryItem(item));
    this.isLoading = false;
  }

  async fetchHistoryInBatches(ids: string[], batchSize: number = 50) {
    const history = [];
    for (let i = 0; i < ids.length; i += batchSize) {
      const batchIds = ids.slice(i, i + batchSize);
      const batchResult = (await this.historyService.find(0, batchIds.length, { $or: [{ dynamicContentId: { $in: batchIds } }, { parentId: { $in: batchIds } }] }))?.data;
      history.push(...batchResult);
    }
    return history;
  }

  private backPage() {
    if (this.entrance) {
      this.router.navigate(['dynamic-content', this.packageId]);
    } else if (this.entry) {
      this.router.navigate(['dynamic-content', this.entry.type, this.entry._id, 'edit'], {
        queryParams: {parentId: this.entranceId, packageId: this.packageId, entranceId: this.entranceId}
      });
    }
  }

  private parseHistoryItem(raw: any): ParsedHistory {
    const oldData = raw.oldData ? JSON.parse(raw.oldData) : null;
    const newData = raw.newData ? JSON.parse(raw.newData) : null;

    const isDeleted = raw.method === 'remove';
    const mainObj = newData || oldData || {};

    const userName = (raw.firstName || raw.lastName) ? `${raw.firstName || ''} ${raw.lastName || ''}`.trim() : raw.email || '';

    return {
      id: raw._id, timestamp: new Date(raw.created), user: { name: userName, email: raw.email }, method: raw.method, itemTitle: mainObj.title || 'Unbenannt',
      itemType: mainObj.type || 'Inhalt', isDeleted, changes: isDeleted ? [] : this.calculateDiff(oldData, newData)
    };
  }

  private calculateDiff(oldData: any, newData: any): FieldChange[] {
    if (!oldData || !newData) return [];

    const changes: FieldChange[] = [];
    const keys = Array.from(new Set([...Object.keys(oldData), ...Object.keys(newData)]));

    const ignoredKeys = [
      '_id', '__v', 'created', 'updated', 'OwnerOrganisationID', 'organisationIds', 'masterLanguageEntry', 'translationEntries', 'children', 'order', 'isNavigationOnly',
      'isMainNavigation', 'codeSnippets'
    ];

    for (const key of keys) {
      if (ignoredKeys.includes(key)) continue;

      const oldVal = oldData[key];
      const newVal = newData[key];

      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        let type: FieldChange['type'] = 'other';
        if (key === 'title') type = 'title';
        else if (key === 'content') type = 'content';
        else if (key === 'mediaFiles') type = 'mediaFiles';
        else if (key === 'dataFiles') type = 'dataFiles';
        else if (key === 'published') type = 'published';

        changes.push({ fieldName: key, type, oldValue: oldVal, newValue: newVal });
      }
    }
    return changes.sort((a, b) => a.type === 'title' ? -1 : (b.type === 'title' ? 1 : 0));
  }
}
